import type {
  BracketTemplate,
  Fixture,
  TournamentBracketData,
  TournamentGroup,
  TournamentGroupTip,
  TournamentStructure,
  TournamentTip,
} from "../types/index.ts";
import type { Profile } from "../supaBaseClient.ts";

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";

export interface TournamentTipWithProfile extends TournamentTip {
  profile?: Profile;
}

// All HTTP work goes through raw fetch — the bundled @supabase/supabase-js
// v2.0.0 hangs on .select() calls in this environment, so we drive the
// PostgREST endpoints directly. For RLS-protected writes we attach the user's
// access token (looked up once via supabase.auth.getSession()).

const anonHeaders = () => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

// Reads the user's access token directly from localStorage. The bundled
// supabase JS client (@supabase/supabase-js@2.0.0) hangs on async calls in
// this environment, including auth.getSession() — but the session itself is
// persisted to localStorage by the same client under a deterministic key, so
// we can grab it synchronously. Falls back to the anon key if no session.
const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // v2.x stores the session as the object directly; older versions wrap it
    // in `{ currentSession }`. Cover both.
    return (
      parsed?.access_token ??
      parsed?.currentSession?.access_token ??
      null
    );
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => {
  const token =
    getAccessTokenFromStorage() ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

export class TournamentService {
  private static instance: TournamentService;
  private constructor() {}

  public static getInstance(): TournamentService {
    if (!TournamentService.instance) {
      TournamentService.instance = new TournamentService();
    }
    return TournamentService.instance;
  }

  async fetchStructure(
    competitionId: number,
  ): Promise<TournamentStructure | null> {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/tournament_structure?competition_id=eq.${competitionId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!response.ok) return null;
      const rows = (await response.json()) as Array<{
        id: number;
        competition_id: number;
        groups: TournamentGroup[];
        bracket_template: BracketTemplate;
      }>;
      if (!rows || rows.length === 0) return null;
      const row = rows[0];
      return {
        id: row.id,
        competition_id: row.competition_id,
        groups: row.groups,
        bracket_template: row.bracket_template,
      };
    } catch (error) {
      console.error("Failed to fetch tournament structure:", error);
      return null;
    }
  }

  async fetchFixtures(competitionId: number): Promise<Fixture[]> {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/fixtures?competition_id=eq.${competitionId}&select=*&order=kickoff_at`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!response.ok) return [];
      return (await response.json()) as Fixture[];
    } catch (error) {
      console.error("Failed to fetch fixtures:", error);
      return [];
    }
  }

  async getUserTip(
    userId: string,
    competitionId: number,
    tipRoundId: number,
  ): Promise<TournamentTip | null> {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_tournament?user_id=eq.${userId}&competition_id=eq.${competitionId}&tip_round_id=eq.${tipRoundId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!response.ok) return null;
      const rows = (await response.json()) as TournamentTip[];
      return rows && rows.length > 0 ? rows[0] : null;
    } catch (error) {
      console.error("Failed to fetch user tournament tip:", error);
      return null;
    }
  }

  async saveUserTip(
    userId: string,
    competitionId: number,
    tipRoundId: number,
    groups: TournamentGroupTip[],
    bracket: TournamentBracketData,
  ): Promise<TournamentTip | null> {
    const payload = {
      user_id: userId,
      competition_id: competitionId,
      tip_round_id: tipRoundId,
      groups,
      bracket,
      updated_at: new Date().toISOString(),
    };
    try {
      const headers = authedHeaders();
      // Upsert via PostgREST. Uniqueness is now on (user, tip_round, comp)
      // — see migration 011.
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_tournament?on_conflict=user_id,tip_round_id,competition_id`,
        {
          method: "POST",
          headers: {
            ...headers,
            Prefer: "resolution=merge-duplicates,return=representation",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Failed to save tournament tip:",
          response.status,
          errorText,
        );
        return null;
      }
      const rows = (await response.json()) as TournamentTip[];
      return rows && rows.length > 0 ? rows[0] : (payload as TournamentTip);
    } catch (error) {
      console.error("Failed to save tournament tip:", error);
      return null;
    }
  }

  async getAllTipsWithProfiles(
    competitionId: number,
    tipRoundId: number,
  ): Promise<TournamentTipWithProfile[]> {
    try {
      const tipsResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_tournament?competition_id=eq.${competitionId}&tip_round_id=eq.${tipRoundId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!tipsResponse.ok) return [];
      const tips = (await tipsResponse.json()) as TournamentTip[];
      if (tips.length === 0) return [];

      const userIds = [...new Set(tips.map((t) => t.user_id))];
      const profilesResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?select=user_id,display_name&user_id=in.(${userIds
          .map((id) => `"${id}"`)
          .join(",")})`,
        { method: "GET", headers: anonHeaders() },
      );
      const profiles = profilesResponse.ok
        ? ((await profilesResponse.json()) as Profile[])
        : [];

      const profileByUser = new Map(profiles.map((p) => [p.user_id, p]));
      return tips.map((tip) => ({
        ...tip,
        profile: profileByUser.get(tip.user_id),
      }));
    } catch (error) {
      console.error("Failed to fetch tournament tips with profiles:", error);
      return [];
    }
  }

  static isTournament(league: { type?: string } | null): boolean {
    return league?.type === "TOURNAMENT";
  }
}

export const tournamentService = TournamentService.getInstance();
